import type { Language } from '@eformulieren/engine'
import { type AdviesToestand, type BelangPartij, BELANG_TOELICHTING_MAX, isOpenAdvies } from '@eformulieren/model'
import { type FormEvent, type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { Dialog } from './Dialog.js'
import { keepEscapeInDialog } from './dialogPlace.js'
import type { AdviceInterestView, AdviceJudgementView } from './adviceModel.js'
import type { ActionOutcome } from './shareModel.js'
import { type Bilingual, formatDateTime } from '../language/text.js'

/**
 * De belangenverklaring op de kaart van de vrager, met het oordeel van NWO (besluit van de gebruiker
 * van 31 oktober 2026, R-184d, R-184j, R-184k, R-139, R-140, ontwerp 7.4a).
 *
 * **Wat er staat.** Wat de adviseur verklaarde en wanneer, zijn toelichting als hij er een gaf, en wat
 * hij zag toen hij verklaarde (de titel, de aanvrager en de mede-aanvragers), achter een uitklapper
 * zodat de kaart kort blijft. Daaronder elk oordeel van NWO, oudste eerst, met wie, wanneer en waarom;
 * bij het laatste staat dat het geldt. Kleur is nooit de enige drager: de uitkomst staat er als woord.
 *
 * **Het oordeel geven** kan als de pod `kanBeoordelen` zegt. De knop opent de modale dialoog "NWO-oordeel
 * geven" (`Dialog`, opdracht van 1 november 2026; tot dan klapte het formulier open in de kaart) met een `fieldset` met twee
 * keuzes, elk met het gevolg voor dit verzoek erbij, en een verplichte toelichting: wie de adviseur
 * tegenspreekt of bevestigt, zegt waarom, want het processysteem en de adviseur lezen het. Zonder keuze
 * of zonder toelichting een melding bij het veld en de focus erop; versturen gaat langs `BusyButton`,
 * en een weigering van de pod staat in de dialoog (R-140). Escape en Annuleren sluiten de dialoog en
 * zetten de focus terug op de knop; tijdens het vastleggen wachten ze.
 *
 * Dit component kent geen route en geen verzoek: het krijgt de verklaring als tekst en een handeling
 * die een uitkomst teruggeeft, zoals `InterestDeclaration`.
 */

type Outcome = 'geen-belang' | 'belang'

export interface InterestReviewProps {
  readonly taal: Language
  /** De toestand van het verzoek: die bepaalt welk gevolg er bij een keuze staat. */
  readonly toestand: AdviesToestand
  readonly verklaring: AdviceInterestView
  /** Oudste eerst; het laatste geldt. */
  readonly oordelen: readonly AdviceJudgementView[]
  readonly kanBeoordelen: boolean
  readonly onOordeel: (uitkomst: Outcome, toelichting: string) => Promise<ActionOutcome>
  /** Een regel voor de statusregio van het paneel als het oordeel is vastgelegd. */
  readonly onStatus: (text: string) => void
  /** Het kopniveau: 4 op een kaart in het paneel (standaard), 3 als de kaart zelf een h2 heeft. */
  readonly kopNiveau?: 3 | 4
}

const T = {
  kop: { nl: 'Belangenverklaring', en: 'Declaration of interest' },
  verklaard: { nl: 'De adviseur verklaarde: {u}', en: 'The adviser declared: {u}' },
  verklaardGeen: { nl: 'geen belang', en: 'no interest' },
  verklaardWel: { nl: 'een belang', en: 'an interest' },
  toelichtingAdviseur: { nl: 'Toelichting van de adviseur: {t}', en: "The adviser's explanation: {t}" },
  zag: { nl: 'Wat de adviseur zag', en: 'What the adviser saw' },
  zagNiets: { nl: 'De adviseur zag geen titel en geen namen.', en: 'The adviser saw no title and no names.' },
  titel: { nl: 'Titel van de aanvraag', en: 'Title of the application' },
  aanvrager: { nl: 'Aanvrager', en: 'Applicant' },
  medeAanvragers: { nl: 'Mede-aanvragers', en: 'Co-applicants' },
  oordelen: { nl: 'NWO-oordelen', en: "NWO's judgements" },
  oordeel: { nl: 'NWO-oordeel: {u}', en: "NWO's judgement: {u}" },
  geen: { nl: 'geen belang', en: 'no interest' },
  wel: { nl: 'wel belang', en: 'an interest' },
  geldt: { nl: 'Dit oordeel geldt.', en: 'This judgement applies.' },
  waarom: { nl: 'Toelichting: {t}', en: 'Explanation: {t}' },
  geven: { nl: 'NWO-oordeel geven', en: "Give NWO's judgement" },
  groep: { nl: 'NWO-oordeel', en: "NWO's judgement" },
  uitleg: {
    nl: 'NWO heeft het laatste woord over het belang van de adviseur. Uw oordeel gaat boven zijn verklaring.',
    en: "NWO has the final say on the adviser's interest. Your judgement prevails over the declaration.",
  },
  geenKeuze: { nl: 'Geen belang', en: 'No interest' },
  welKeuze: { nl: 'Wel belang', en: 'An interest' },
  geenLoopt: { nl: 'Het verzoek loopt door; de adviseur kan advies geven.', en: 'The request continues; the adviser can give advice.' },
  geenWeer: {
    nl: 'Het verzoek loopt weer en de adviseur kan het formulier lezen en advies geven. Hij hoort het van NWO.',
    en: 'The request continues again and the adviser can read the form and give advice. NWO informs the adviser.',
  },
  welEindigt: {
    nl: 'Het verzoek eindigt en de adviseur heeft geen toegang meer. Hij hoort het van NWO.',
    en: 'The request ends and the adviser no longer has access. NWO informs the adviser.',
  },
  welBlijft: { nl: 'Het verzoek blijft beëindigd; de adviseur krijgt geen toegang.', en: 'The request stays ended; the adviser gets no access.' },
  toelichting: { nl: 'Toelichting', en: 'Explanation' },
  toelichtingHulp: {
    nl: 'Verplicht. Waarom oordeelt NWO zo? Het processysteem en de adviseur lezen dit.',
    en: 'Required. Why does NWO judge this way? The process system and the adviser read this.',
  },
  kiesEerst: { nl: 'Kies geen belang of wel belang.', en: 'Choose no interest or an interest.' },
  toelichtingLeeg: { nl: 'Schrijf waarom NWO zo oordeelt.', en: 'Write why NWO judges this way.' },
  vastleggen: { nl: 'Oordeel vastleggen', en: 'Record judgement' },
  bezig: { nl: 'Bezig met vastleggen…', en: 'Recording…' },
  annuleren: { nl: 'Annuleren', en: 'Cancel' },
  vastgelegdGeen: {
    nl: 'Het NWO-oordeel is vastgelegd: geen belang. De adviseur kan het formulier weer lezen en advies geven.',
    en: "NWO's judgement has been recorded: no interest. The adviser can read the form and give advice again.",
  },
  vastgelegdGeenLoopt: { nl: 'Het NWO-oordeel is vastgelegd: geen belang.', en: "NWO's judgement has been recorded: no interest." },
  vastgelegdWel: {
    nl: 'Het NWO-oordeel is vastgelegd: wel belang. Het verzoek is beëindigd.',
    en: "NWO's judgement has been recorded: an interest. The request has ended.",
  },
} as const satisfies Readonly<Record<string, Bilingual>>

function line(party: BelangPartij): string {
  return party.organisatie === null ? party.naam : `${party.naam}, ${party.organisatie}`
}

/** "4 dec 2026 09:00" in Nederland. */
function momentText(moment: string, language: Language): string {
  return formatDateTime(moment, language, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Amsterdam',
  })
}

export function InterestReview(props: InterestReviewProps): ReactNode {
  const { taal: language, toestand: state, verklaring: declaration, oordelen: judgements } = props
  const id = useId()
  const Heading = props.kopNiveau === 3 ? 'h3' : 'h4'
  const [open, setOpen] = useState(false)
  const [choice, setChoice] = useState<Outcome | null>(null)
  const [explanation, setExplanation] = useState('')
  const [problem, setProblem] = useState<'keuze' | 'toelichting' | 'pod' | undefined>(undefined)
  const [podText, setPodText] = useState('')
  const firstChoice = useRef<HTMLInputElement>(null)
  const box = useRef<HTMLTextAreaElement>(null)
  const giveButton = useRef<HTMLButtonElement>(null)
  const [restore, setRestore] = useState(false)
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    if (!restore || open) return
    giveButton.current?.focus()
    setRestore(false)
  }, [restore, open])

  const running = isOpenAdvies(state)
  const parties = declaration.partijen
  const known = parties !== null && (parties.titel !== null || parties.aanvrager !== null || parties.medeAanvragers.length > 0)
  const consequence = (outcome: Outcome): string =>
    outcome === 'geen-belang' ? (running ? T.geenLoopt : T.geenWeer)[language] : (running ? T.welEindigt : T.welBlijft)[language]

  function close(): void {
    setOpen(false)
    setChoice(null)
    setExplanation('')
    setProblem(undefined)
  }

  /** Zonder keuze of toelichting geen belofte: dan wordt de knop niet bezig en staat de melding er meteen. */
  function record(): Promise<void> | undefined {
    if (choice === null) {
      setProblem('keuze')
      firstChoice.current?.focus()
      return undefined
    }
    const text = explanation.trim()
    if (text === '') {
      setProblem('toelichting')
      box.current?.focus()
      return undefined
    }
    setProblem(undefined)
    setBusy(true)
    return send(choice, text).finally(() => setBusy(false))
  }

  async function send(choice: Outcome, text: string): Promise<void> {
    const outcome = await props.onOordeel(choice, text)
    if (outcome.soort !== 'gelukt') {
      setPodText(outcome.melding.tekst)
      setProblem('pod')
      return
    }
    props.onStatus((choice === 'belang' ? T.vastgelegdWel : running ? T.vastgelegdGeenLoopt : T.vastgelegdGeen)[language])
    close()
    setRestore(true)
  }

  return (
    <section className="ef-belangoordeel" aria-labelledby={`${id}-kop`} lang={language}>
      <Heading id={`${id}-kop`} className="ef-belangoordeel__kop">
        {T.kop[language]}
      </Heading>
      <p className="ef-belangoordeel__regel">
        {T.verklaard[language].replace('{u}', (declaration.uitkomst === 'belang' ? T.verklaardWel : T.verklaardGeen)[language])}
        {' · '}
        <time dateTime={declaration.moment}>{momentText(declaration.moment, language)}</time>
      </p>
      {declaration.toelichting === null ? null : (
        <p className="ef-adviesverzoek__belang">{T.toelichtingAdviseur[language].replace('{t}', declaration.toelichting)}</p>
      )}
      <details className="ef-belangoordeel__zag">
        <summary className="ef-belangoordeel__zagknop">{T.zag[language]}</summary>
        {known && parties !== null ? (
          <dl className="ef-belang__lijst">
            {parties.titel === null ? null : (
              <div className="ef-belang__regel">
                <dt>{T.titel[language]}</dt>
                <dd>{parties.titel}</dd>
              </div>
            )}
            {parties.aanvrager === null ? null : (
              <div className="ef-belang__regel">
                <dt>{T.aanvrager[language]}</dt>
                <dd>{line(parties.aanvrager)}</dd>
              </div>
            )}
            {parties.medeAanvragers.length === 0 ? null : (
              <div className="ef-belang__regel">
                <dt>{T.medeAanvragers[language]}</dt>
                <dd>
                  <ul className="ef-belang__namen">
                    {parties.medeAanvragers.map((party, index) => (
                      <li key={index}>{line(party)}</li>
                    ))}
                  </ul>
                </dd>
              </div>
            )}
          </dl>
        ) : (
          <p className="ef-belangoordeel__regel">{T.zagNiets[language]}</p>
        )}
      </details>

      {judgements.length === 0 ? null : (
        <ol className="ef-belangoordeel__lijst" aria-label={T.oordelen[language]}>
          {judgements.map((judgement, index) => (
            <li key={`${judgement.moment}-${String(index)}`} className="ef-belangoordeel__oordeel" data-uitkomst={judgement.uitkomst}>
              <p className="ef-belangoordeel__regel">
                <strong>{T.oordeel[language].replace('{u}', (judgement.uitkomst === 'belang' ? T.wel : T.geen)[language])}</strong>
                {' · '}
                {judgement.wie}
                {' · '}
                <time dateTime={judgement.moment}>{momentText(judgement.moment, language)}</time>
              </p>
              <p className="ef-belangoordeel__toelichting">{T.waarom[language].replace('{t}', judgement.toelichting)}</p>
              {index === judgements.length - 1 ? <p className="ef-belangoordeel__geldt">{T.geldt[language]}</p> : null}
            </li>
          ))}
        </ol>
      )}

      {/* Het oordeel gaat in een dialoog, dus de knop blijft staan en de focus kan ernaar terug. */}
      {props.kanBeoordelen ? (
        <div className="ef-adviesverzoek__acties">
          <button
            ref={giveButton}
            type="button"
            className="ef-actieknop"
            data-soort="secundair"
            aria-haspopup="dialog"
            onClick={() => {
              setProblem(undefined)
              setOpen(true)
            }}
          >
            {T.geven[language]}
          </button>
        </div>
      ) : null}

      {props.kanBeoordelen && open ? (
        // Escape sluit alleen de dialoog en niet het zijpaneel Advies eromheen.
        <div className="ef-dialooghouder" onKeyDown={keepEscapeInDialog}>
        <Dialog
          taal={language}
          titel={T.geven[language]}
          onSluit={() => {
            // Tijdens het vastleggen wacht sluiten tot de pod antwoordt.
            if (busy) return
            close()
            setRestore(true)
          }}
          secundair={{ label: T.annuleren[language], uitgeschakeld: busy }}
          primair={{ label: T.vastleggen[language], bezigTekst: T.bezig[language], onActie: record }}
        >
        <form
          className="ef-belang__formulier"
          noValidate
          onSubmit={(event: FormEvent) => {
            // De knop legt zelf vast (BusyButton); Enter doet niets dubbel.
            event.preventDefault()
          }}
        >
          <fieldset className="ef-belang__keuzes" aria-describedby={[`${id}-uitleg`, problem === 'keuze' ? `${id}-keuze-mist` : undefined].filter(Boolean).join(' ')}>
            <legend className="ef-belang__legenda">{T.groep[language]}</legend>
            <p id={`${id}-uitleg`} className="ef-belang__gevolg">
              {T.uitleg[language]}
            </p>
            {(['geen-belang', 'belang'] as const).map((outcome, index) => (
              <div key={outcome} className="ef-belang__keuze">
                <input
                  ref={index === 0 ? firstChoice : undefined}
                  {...(index === 0 ? { 'data-autofocus': true } : {})}
                  id={`${id}-${outcome}`}
                  type="radio"
                  name={`${id}-uitkomst`}
                  value={outcome}
                  checked={choice === outcome}
                  aria-describedby={`${id}-${outcome}-gevolg`}
                  onChange={() => {
                    setChoice(outcome)
                    if (problem === 'keuze') setProblem(undefined)
                  }}
                />
                <span className="ef-belang__keuzetekst">
                  <label htmlFor={`${id}-${outcome}`} className="ef-belang__keuzelabel">
                    {(outcome === 'geen-belang' ? T.geenKeuze : T.welKeuze)[language]}
                  </label>
                  <span id={`${id}-${outcome}-gevolg`} className="ef-belang__gevolg">
                    {consequence(outcome)}
                  </span>
                </span>
              </div>
            ))}
            {problem === 'keuze' ? (
              <p id={`${id}-keuze-mist`} role="alert" className="ef-belang__melding">
                {T.kiesEerst[language]}
              </p>
            ) : null}
          </fieldset>
          <div className="ef-belang__toelichting">
            <label htmlFor={`${id}-toelichting`} className="ef-belang__label">
              {T.toelichting[language]}
            </label>
            <p id={`${id}-toelichting-hulp`} className="ef-belang__gevolg">
              {T.toelichtingHulp[language]}
            </p>
            <textarea
              ref={box}
              id={`${id}-toelichting`}
              className="ef-belang__invoer"
              rows={4}
              required
              maxLength={BELANG_TOELICHTING_MAX}
              value={explanation}
              onChange={(event) => {
                setExplanation(event.target.value)
                if (problem === 'toelichting') setProblem(undefined)
              }}
              aria-describedby={[`${id}-toelichting-hulp`, problem === 'toelichting' ? `${id}-toelichting-mist` : undefined].filter(Boolean).join(' ')}
              {...(problem === 'toelichting' ? { 'aria-invalid': true } : {})}
            />
            {problem === 'toelichting' ? (
              <p id={`${id}-toelichting-mist`} role="alert" className="ef-belang__melding">
                {T.toelichtingLeeg[language]}
              </p>
            ) : null}
          </div>
          {problem === 'pod' ? (
            <p role="alert" className="ef-veld__melding--hard">
              {podText}
            </p>
          ) : null}
        </form>
        </Dialog>
        </div>
      ) : null}
    </section>
  )
}
